import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CheckCircle, XCircle, Clock, Home, UserRound } from 'lucide-react';
import LoadingIndicator from '@/components/LoadingIndicator';
import './VerifyRedeemPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyRedeemPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/redeem/verify/${token}`)
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData({ valid: false, reason: 'network', message: 'No se pudo verificar.' }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  if (loading) {
    return (
      <LoadingIndicator fullScreen label="Verificando código..." className="p-6" />
    );
  }

  if (!data?.valid) {
    const expired = data?.reason === 'expired';
    const networkError = data?.reason === 'network';
    const ErrorIcon = networkError ? AlertTriangle : XCircle;
    return (
      <div className="verify-redeem verify-redeem--invalid" data-testid="verify-invalid">
        <div className="verify-redeem__content">
          <section className={`verify-redeem__error-card ${expired ? 'verify-redeem__error-card--expired' : ''} ${networkError ? 'verify-redeem__error-card--technical' : ''}`}>
            <div className={`verify-redeem__status-icon ${networkError ? 'verify-redeem__status-icon--technical' : 'verify-redeem__status-icon--inactive'}`} aria-hidden="true">
              <ErrorIcon className="w-8 h-8" />
            </div>
            <p className="verify-redeem__eyebrow">Verificación de beneficio</p>
            <h1 className="verify-redeem__title">
              {expired ? 'Código vencido' : networkError ? 'No pudimos validar este beneficio' : 'Código inválido'}
            </h1>
            <p className="verify-redeem__description">
              {expired
                ? 'Pedile al usuario que genere uno nuevo desde la app.'
                : data?.message || 'El código no se pudo verificar.'}
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={`verify-redeem verify-redeem--valid ${!data.is_active ? 'verify-redeem--inactive' : ''}`} data-testid="verify-valid">
      <div className="verify-redeem__content">
        <section className="verify-redeem__status" aria-live="polite">
          <div className={`verify-redeem__status-icon ${data.is_active ? 'verify-redeem__status-icon--active' : 'verify-redeem__status-icon--inactive'}`} aria-hidden="true">
            {data.is_active ? <CheckCircle className="w-8 h-8" /> : <XCircle className="w-8 h-8" />}
          </div>
          <p className="verify-redeem__eyebrow">WannaColive</p>
          <h1 className="verify-redeem__title">
            {data.is_active ? 'Usuario verificado' : 'Usuario inactivo'}
          </h1>
          <p className="verify-redeem__description">
            {data.is_active ? 'Beneficio válido' : 'El beneficio no está activo'}
          </p>
        </section>

        <section className="verify-redeem__identity" aria-label="Datos del usuario">
          <div className="verify-redeem__identity-main" data-testid="verify-name">
            <UserRound className="verify-redeem__identity-icon" aria-hidden="true" />
            <div className="min-w-0">
              <p className="verify-redeem__identity-name">{data.name}</p>
              <p className="verify-redeem__identity-house" data-testid="verify-house">
                <Home className="w-3.5 h-3.5" aria-hidden="true" />
                {data.house_name}
              </p>
            </div>
          </div>
          <div className={`verify-redeem__status-pill ${data.is_active ? 'verify-redeem__status-pill--active' : 'verify-redeem__status-pill--inactive'}`} data-testid="verify-status">
            {data.is_active ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {data.is_active ? 'ACTIVO ✓' : 'NO ACTIVO ✕'}
          </div>
        </section>

        {data.benefit_title && (
          <section className="verify-redeem__benefit" aria-label="Beneficio">
            <div className="verify-redeem__benefit-copy">
              <p className="verify-redeem__section-label">Beneficio</p>
              <h2 className="verify-redeem__benefit-title">{data.benefit_title}</h2>
              {data.business && <p className="verify-redeem__benefit-business">{data.business}</p>}
            </div>
            {data.discount && <div className="verify-redeem__discount">{data.discount} <span>OFF</span></div>}
          </section>
        )}

        <div className="verify-redeem__expiry">
          <Clock className="w-4 h-4" aria-hidden="true" />
          <div>
            <p>Válido por 5 minutos</p>
            <span>Si vence, el usuario puede generar uno nuevo desde la app.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
